export interface Permission {
  id: number
  name: string
  codename: string
  app_label: string
  model: string
  full_codename: string
}

export type Role = {
  id: string
  name: string
  description: string
  updatesAllowed: boolean
  isEnabled?: boolean
  permissions: string[]
}

export type PermissionCategory = {
  name: string
  permissions: Permission[]
}

// Helper function to categorize permissions
export function categorizePermissions(
  permissions: Permission[]
): PermissionCategory[] {
  const categories = new Map<string, Permission[]>()

  permissions.forEach((permission) => {
    // Use the model attribute for grouping (e.g., "auditlog" -> "Auditlog")
    // Fallback to app_label if model is missing
    const rawCategory = permission.model || permission.app_label || 'other';
    
    // Capitalize the first letter: "auditlog" -> "Auditlog"
    const category = rawCategory.charAt(0).toUpperCase() + rawCategory.slice(1);
    
    if (!categories.has(category)) {
      categories.set(category, [])
    }
    categories.get(category)!.push(permission)
  })

  return Array.from(categories.entries())
    .map(([name, perms]) => ({
      name,
      permissions: perms.sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
}


// Helper to get permission action (READ, CREATE, UPDATE, DELETE)
// Example: codename "add_auditlog" -> "ADD" -> "CREATE"
export function getPermissionAction(permission: Permission): string {
  if (!permission?.codename) return 'UNKNOWN';
  
  const action = permission.codename.split('_')[0].toUpperCase();

  const mapping: Record<string, string> = {
    VIEW: 'READ',
    ADD: 'CREATE',
    CHANGE: 'UPDATE',
    DELETE: 'DELETE'
  }

  return mapping[action] || action
}

// Helper to get permission resource (USER, ROLE, ENGINE, INVESTOR)
export function getPermissionResource(permission: Permission): string {
    return (permission.model || permission.app_label || 'UNKNOWN').toUpperCase();
}
